// Authentication types

/**
 * User roles with different permission levels:
 * - admin: Full access to everything
 * - designer: Read content (PRF/Hooks), write/edit assets, can schedule
 * - user: View episode folders, download only, no content visible (sales rep)
 */
export type UserRole = 'admin' | 'designer' | 'user'

export interface User {
  _id: string
  name: string
  email: string
  role?: UserRole
  createdAt: string
  lastLoginAt?: string
}

/**
 * Permission definitions by role
 */
export const ROLE_PERMISSIONS = {
  admin: {
    canViewContent: true,    // View PRF, Hooks, Posts, Transcript
    canEditContent: true,    // Edit PRF, Hooks, Posts, Transcript
    canViewAssets: true,     // View all assets
    canEditAssets: true,     // Review, approve, reject assets
    canSchedule: true,       // Schedule posts and assets
    canDownload: true,       // Download assets
    canManageUsers: true,    // Manage team members
  },
  designer: {
    canViewContent: true,    // Can view PRF/Hooks for context
    canEditContent: false,   // Cannot edit text content
    canViewAssets: true,     // View all assets
    canEditAssets: true,     // Review, approve, reject assets
    canSchedule: true,       // Schedule assets
    canDownload: true,       // Download assets
    canManageUsers: false,   // Cannot manage team
  },
  user: {
    canViewContent: false,   // Cannot see PRF, Hooks, Posts
    canEditContent: false,   // Cannot edit content
    canViewAssets: true,     // Can view approved assets only
    canEditAssets: false,    // Cannot review/approve assets
    canSchedule: false,      // Cannot schedule
    canDownload: true,       // Can download approved assets
    canManageUsers: false,   // Cannot manage team
  },
} as const

export type Permission = keyof typeof ROLE_PERMISSIONS.admin

export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface CreateUserInput {
  name: string
  email: string
  password: string
}
